import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'

export const Route = createFileRoute('/onboarding/email')({
  component: GetStartedStep,
})

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'mail.com',
  'yandex.com',
  'zoho.com',
  'fastmail.com',
  'tutanota.com',
  'gmx.com',
  'me.com',
  'mac.com',
  'msn.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'yahoo.fr',
  'yahoo.de',
  'outlook.fr',
  'outlook.de',
  'outlook.co.uk',
])

function isPersonalEmail(emailAddress: string): boolean {
  const domain = emailAddress.split('@')[1]?.toLowerCase()
  return domain ? PERSONAL_EMAIL_DOMAINS.has(domain) : false
}

function cleanDomain(input: string): string {
  let cleaned = input.trim().toLowerCase()
  // Strip protocol
  cleaned = cleaned.replace(/^https?:\/\//, '')
  // Strip www.
  cleaned = cleaned.replace(/^www\./, '')
  // Strip trailing slashes and paths
  cleaned = cleaned.split('/')[0] ?? cleaned
  return cleaned
}

const COMPANY_WEBSITE_ERROR_MESSAGE = 'Please enter a valid company website'
const MAX_DOMAIN_LENGTH = 253
const MAX_DOMAIN_LABEL_LENGTH = 63
const DOMAIN_LABEL_RE = /^[a-z0-9-]+$/
const DOMAIN_TLD_RE = /^[a-z]{2,}$/

function isValidCompanyDomain(input: string): boolean {
  const cleaned = cleanDomain(input)
  if (!cleaned || cleaned.length > MAX_DOMAIN_LENGTH || !cleaned.includes('.'))
    return false
  return cleaned.split('.').every((label, index, labels) => {
    const isLast = index === labels.length - 1
    if (!label || label.length > MAX_DOMAIN_LABEL_LENGTH)
      return false
    if (!DOMAIN_LABEL_RE.test(label) || label.startsWith('-') || label.endsWith('-'))
      return false
    return !isLast || DOMAIN_TLD_RE.test(label)
  })
}

function companyDomainError(input: string): string | undefined {
  return isValidCompanyDomain(input) ? undefined : COMPANY_WEBSITE_ERROR_MESSAGE
}

function GetStartedStep() {
  useOnboardingStepViewed('email')
  const navigate = useNavigate()
  const {
    email: savedEmail,
    firstName: savedFirstName,
    lastName: savedLastName,
    company: savedCompany,
    domain: savedDomain,
    password: savedPassword,
    onboardingMode,
    currentStep,
    inviteToken,
    updateState,
  } = useOnboarding()
  const isMember = onboardingMode === 'member'
  // Partner invitees reach this page via /onboarding/partner-invite, which prefills
  // email + name and sets inviteToken. Backend validates that the submitted email
  // matches the one on the invite, so we lock the field to prevent a 422.
  const emailIsLocked = !isMember && !!inviteToken && !!savedEmail
  // Only show the resume banner when state was advanced past /email AND the
  // provider's password is actually missing — a back-nav from a later step
  // keeps the password in React memory, so the banner would be misleading there.
  const isResuming = currentStep > 1 && !savedPassword

  const [email, setEmail] = useState(savedEmail || '')
  const [firstName, setFirstName] = useState(savedFirstName || '')
  const [lastName, setLastName] = useState(savedLastName || '')
  const [companyName, setCompanyName] = useState(savedCompany.name || '')
  const [password, setPassword] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState(savedDomain || '')
  const [emailError, setEmailError] = useState<string | undefined>()
  const [websiteError, setWebsiteError] = useState<string | undefined>()

  const isValid = isMember
    ? firstName.trim() !== '' && lastName.trim() !== '' && password.length >= 8
    : email.trim() !== ''
      // Trust the prefilled email on the invitee flow — the backend already
      // rejected personal domains when the invite was created, and the field
      // is locked so the user can't change it. Running the frontend's
      // PERSONAL_EMAIL_DOMAINS check here risks disabling submit forever if
      // the two lists drift.
      && (emailIsLocked || !isPersonalEmail(email))
      && !emailError
      && firstName.trim() !== ''
      && lastName.trim() !== ''
      && companyName.trim() !== ''
      && password.length >= 8
      && websiteUrl.trim() !== ''
      && !companyDomainError(websiteUrl)

  const handleSubmit = useCallback(() => {
    const trimmedFirst = firstName.trim()
    const trimmedLast = lastName.trim()
    const cleanedDomain = cleanDomain(websiteUrl)

    if (isMember) {
      updateState({
        firstName: trimmedFirst,
        lastName: trimmedLast,
        password,
        memberProfile: { name: [trimmedFirst, trimmedLast].filter(Boolean).join(' '), role: '' },
        currentStep: 2,
      })
      navigate({ to: '/onboarding/claim-territory' })
      return
    }

    updateState({
      email: email.trim(),
      firstName: trimmedFirst,
      lastName: trimmedLast,
      password,
      domain: cleanedDomain,
      company: { ...savedCompany, name: companyName.trim(), domain: cleanedDomain },
      memberProfile: { name: [trimmedFirst, trimmedLast].filter(Boolean).join(' '), role: '' },
      currentStep: 2,
    })
    navigate({ to: '/onboarding/ideal-customer' })
  }, [email, firstName, lastName, companyName, password, websiteUrl, savedCompany, isMember, updateState, navigate])

  return (
    <div className="flex flex-1 items-center justify-center py-16">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Get started</CardTitle>
          <p className="text-sm text-muted-foreground">
            {isMember ? 'Set up your account' : 'Tell us about yourself and your company'}
          </p>
        </CardHeader>
        <CardContent>
          {isResuming && (
            <Alert className="mb-4">
              <AlertDescription>
                Please re-enter your password to pick up where you left off.
              </AlertDescription>
            </Alert>
          )}
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (isValid)
                handleSubmit()
            }}
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                />
              </div>
            </div>

            {!isMember && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="companyName">Company name</Label>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="website">Company website</Label>
                  <Input
                    id="website"
                    type="text"
                    placeholder="yourcompany.com"
                    value={websiteUrl}
                    aria-invalid={websiteError ? true : undefined}
                    aria-describedby={websiteError ? 'website-error' : undefined}
                    onChange={(e) => {
                      const nextValue = e.target.value
                      setWebsiteUrl(nextValue)
                      if (websiteError)
                        setWebsiteError(companyDomainError(nextValue))
                    }}
                    onBlur={() => {
                      if (websiteUrl)
                        setWebsiteError(companyDomainError(websiteUrl))
                    }}
                  />
                  {websiteError && (
                    <p id="website-error" className="text-xs text-destructive">{websiteError}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email">Professional email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    readOnly={emailIsLocked}
                    onChange={(e) => {
                      if (emailIsLocked)
                        return
                      setEmail(e.target.value)
                      if (emailError)
                        setEmailError(undefined)
                    }}
                    onBlur={() => {
                      if (!emailIsLocked && email && isPersonalEmail(email))
                        setEmailError('Please use a professional email address')
                    }}
                  />
                  {emailError && (
                    <p className="text-xs text-destructive">{emailError}</p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Min 8 characters</p>
            </div>

            <Button type="submit" className="w-full" disabled={!isValid}>
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
